import React, { useState, useEffect, useRef } from "react";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useDebounce } from "use-debounce";
import {
  buildSearchIndex,
  buildSearchIndexFromManifest,
} from "../../utils/searchIndex";
import { dataCategories } from "../../config/categories";
import Fuse from "fuse.js";

const SearchBar = ({ onSelectIndicator, className = "" }) => {
  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebounce(query, 300);
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  const [searchIndex, setSearchIndex] = useState([]);
  const [fuse, setFuse] = useState(null);

  // Load search index
  useEffect(() => {
    let cancelled = false;

    const loadIndex = async () => {
      try {
        // Try to load from manifest first
        const response = await fetch("/json/indicator_manifest.json");
        if (!response.ok) throw new Error("Failed to load manifest");
        const manifest = await response.json();

        if (cancelled) return;

        const index = buildSearchIndexFromManifest(manifest, dataCategories);
        setSearchIndex(index);
      } catch (err) {
        console.warn("Falling back to static search index", err);
        // Fallback to static index if available or empty
        const staticIndex = buildSearchIndex();
        if (!cancelled) setSearchIndex(staticIndex);
      }
    };

    loadIndex();
    return () => {
      cancelled = true;
    };
  }, []);

  // Initialize Fuse.js when index is ready
  useEffect(() => {
    if (searchIndex.length > 0) {
      const fuseInstance = new Fuse(searchIndex, {
        keys: ["name", "category", "keywords"],
        threshold: 0.3,
        distance: 100,
        minMatchCharLength: 2,
        shouldSort: true,
        includeScore: true,
      });
      setFuse(fuseInstance);
    }
  }, [searchIndex]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Search logic
  useEffect(() => {
    if (!fuse || debouncedQuery.trim().length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    const results = fuse
      .search(debouncedQuery)
      .slice(0, 10)
      .map((result) => result.item);
    setSuggestions(results);
    setIsOpen(true);
    setSelectedIndex(-1);
  }, [debouncedQuery, fuse]);

  const handleKeyDown = (e) => {
    if (!isOpen) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelect(suggestions[selectedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleSelect = (item) => {
    if (onSelectIndicator) {
      onSelectIndicator({
        indicator: item.name,
        category: item.categoryId,
      });
    }
    setQuery("");
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleClear = () => {
    setQuery("");
    setSuggestions([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div ref={searchRef} className={`relative z-30 ${className}`}>
      <div className="relative group">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 group-focus-within:text-trac-500 transition-colors" />
        </div>

        <input
          ref={inputRef}
          type="text"
          className="block w-full pl-11 pr-10 py-3.5 bg-white border border-gray-200 rounded-xl leading-5
                     placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-trac-500/20 focus:border-trac-500
                     transition-all duration-200 shadow-sm text-gray-900"
          placeholder="Search for indicators (e.g. literacy, health, employment)..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) setIsOpen(true);
          }}
        />

        {query && (
          <button
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
          >
            <div className="p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
              <XMarkIcon className="h-5 w-5" />
            </div>
          </button>
        )}
      </div>

      {/* Results Dropdown */}
      {isOpen && (
        <div className="absolute mt-2 w-full bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden animate-in fade-in zoom-in-95 duration-100 origin-top">
          {suggestions.length > 0 ? (
            <>
              <div className="max-h-[60vh] overflow-y-auto py-2">
                <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Suggested Indicators
                </div>
                {suggestions.map((item, index) => (
                  <div
                    key={`${item.categoryId}-${item.name}-${index}`}
                    onClick={() => handleSelect(item)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`
                      px-4 py-3 cursor-pointer flex items-start gap-3 transition-colors
                      ${index === selectedIndex ? "bg-trac-50" : "hover:bg-gray-50"}
                    `}
                  >
                    <div
                      className={`mt-1 flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-lg bg-gray-50 border border-gray-100`}
                    >
                      {item.icon || "📊"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium ${index === selectedIndex ? "text-trac-900" : "text-gray-900"}`}
                      >
                        {item.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                        <span className="truncate">
                          {item.category || "General"}
                        </span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 text-xs text-gray-400 flex justify-between items-center">
                <span>{suggestions.length} results found</span>
                <span className="hidden sm:inline">
                  Use ↑↓ to navigate, Enter to select
                </span>
              </div>
            </>
          ) : (
            <div className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-50 mb-3">
                <MagnifyingGlassIcon className="h-6 w-6 text-gray-400" />
              </div>
              <h3 className="text-sm font-medium text-gray-900">
                No matching indicators
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Try adjusting your search terms or browse by category.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
